// Yoga Classes - Core Page (Using client's exact wording)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, MapPin, Check } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../components/shared/SEOHelper";
import { ReviewsStrip, getReviewsForPage } from "../components/shared/Reviews";
import { BookwhenEmbed } from "../components/shared/BookwhenEmbed";

const YogaClassesPage = () => {
  usePageMeta({
    title: "Yoga Classes | A Space to Move, Breathe & Calm Your Mind",
    description: "Yoga classes in Frome and Exeter with Annelie Lily. A space to slow down, come out of the mind and back into the body.",
    canonical: "https://annelielily.com/yoga-classes"
  });

  const serviceSchema = generateServiceSchema({
    name: "Yoga Classes",
    description: "Small group yoga classes in Frome and Exeter. Grounded, calm and supportive classes for all levels with British Wheel of Yoga teacher Annelie Lily.",
    serviceType: "Yoga Class",
    url: "https://annelielily.com/yoga-classes"
  });

  // Yoga styles from client's email - with links to child pages
  const yogaStyles = [
    { name: "Yin yoga", link: "/yoga-classes/yin-yoga" },
    { name: "Hatha yoga", link: "/yoga-classes/hatha-yoga" },
    { name: "Restorative yoga", link: null },
    { name: "Yoga Nidra", link: null },
    { name: "Kundalini-inspired practices", link: null }
  ];

  // What classes help with (from client's email)
  const classesHelpWith = [
    "Release tension in the body",
    "Calm the nervous system",
    "Reconnect with your breath",
    "Build awareness and presence"
  ];

  // Ways to practice with Annelie (from client's email)
  const waysToPrice = [
    {
      title: "1:1 & Private Yoga",
      description: "Individual sessions tailored to your needs. These can support you with building confidence, working with injuries, deepening your practice or having a more focused and personal experience.",
      link: "/yoga-classes/private-yoga"
    },
    {
      title: "SEN Yoga",
      description: "Gentle, supportive sessions designed for those with additional needs. These sessions are adapted to the individual and focus on creating a safe, calm and accessible space.",
      link: "/yoga-classes/sen-yoga"
    },
    {
      title: "Children & Family Yoga",
      description: "Playful and grounding sessions that support connection, movement and relaxation for both children and families.",
      link: "/yoga-classes/children-family-yoga"
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga Classes" currentSlug="yoga-classes" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Yoga Classes
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              A Space to Move, Breathe, Reconnect & Calm Your Mind
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              My yoga classes are a place to slow down, come out of the mind and back into the body.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Users className="w-4 h-4 text-gold" />
                Small groups
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Frome / Exeter
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary" data-testid="yoga-classes-book-btn">
                Book Now <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/yoga-classes/private-yoga" className="btn-secondary">
                Enquire about Private Sessions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Book a Class - Bookwhen (top placement) */}
      <BookwhenEmbed
        slug="annelielily"
        title="Book a Class"
        subtitle="Choose a class below and reserve your place in seconds."
        bgClass="bg-cream"
        testId="yoga-classes-bookwhen"
      />

      {/* Opening Section - Annelie + the space */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-6 mb-12"
          >
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/4jgjmxzw_8A846CC2-0964-4DDB-A78E-566538DC390B.PNG"
                alt="Annelie Lily in tree pose (Vrksasana) inside the warm loft yoga room of her Grade II listed cottage in Frome, with fairy lights, wooden beams and golden afternoon light"
                className="w-full h-full object-cover"
                width={600}
                height={600}
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/85ch7iny_IMG_2885.jpg"
                alt="The warm loft yoga room inside Annelie's Grade II listed cottage in Frome with stone wall, Buddha artwork, chakra hanging, sunset painting and two yoga mats with bolsters under soft lamp light"
                className="w-full h-full object-cover"
                width={600}
                height={600}
                loading="eager"
                decoding="async"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Yoga for Mind, Body & Spirit
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Held in the warm loft room of a beautiful, Grade II listed cottage on the outskirts of Frome — a space designed to help you move gently, breathe more deeply, relax and release tension in your body.
              </p>
              <p>
                My yoga classes are not about pushing or achieving; they are about listening to and working with your body.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* About the Classes */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              About the Classes
            </h2>
            <div className="prose-content space-y-4 text-neutral-700 mb-8">
              <p>
                Classes are grounded, calm and supportive.
              </p>
              <p>
                You will be guided through movement, breath and stillness in a way that helps you to:
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {classesHelpWith.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 bg-white/70 p-4 rounded-xl border border-gold/10"
                >
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Styles of Yoga */}
      <section id="styles" className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Styles of Yoga
            </h2>
            <p className="text-neutral-700 mb-6">
              I draw on a range of practices depending on the class and the person and what is needed in the space, including:
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              {yogaStyles.map((style, index) => (
                style.link ? (
                  <Link 
                    key={index}
                    to={style.link}
                    className="bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-medium hover:bg-gold/20 transition-colors"
                  >
                    {style.name}
                  </Link>
                ) : (
                  <span 
                    key={index}
                    className="bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-medium"
                  >
                    {style.name}
                  </span>
                )
              ))}
            </div>
            <p className="text-neutral-600 italic">
              Each class is slightly different, but all are designed to support balance, awareness and connection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ways to Practice with Me */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ways to Practice with Me
            </h2>
            <p className="text-neutral-600">
              In addition to group classes, I also offer more personalised and specialist sessions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {waysToPrice.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={item.link}
                  className="block bg-white/80 p-6 rounded-2xl border border-gold/10 hover:border-gold/30 transition-all h-full group"
                >
                  <h3 className="font-heading text-xl text-neutral-800 mb-3 group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">{item.description}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Classes Are For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Who the Classes Are For
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                These classes and sessions are open to both men and women.
              </p>
              <p>
                They are suitable for all levels, including beginners.
              </p>
              <p>
                You do not need to be flexible or experienced.
              </p>
              <p className="text-lg font-medium text-neutral-800 bg-gold/5 p-5 rounded-xl border border-gold/10">
                You are welcome exactly as you are.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* More Than Movement */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              More Than Movement
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                For many people, yoga becomes more than just a class.
              </p>
              <p>
                It can be a way of reconnecting with yourself, slowing down and of beginning to notice what is happening beneath the surface.
              </p>
              <p>
                I aim to help people calm their mind and body.
              </p>
              <p className="italic text-neutral-600">
                You are free to take it at your own pace.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Practical Details */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Class Details
            </h2>
            <div className="bg-white/70 p-6 rounded-xl border border-gold/10 space-y-4">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-neutral-800">Locations</p>
                  <p className="text-neutral-600">Frome / Exeter</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-5 h-5 text-gold flex-shrink-0 mt-1 flex items-center justify-center font-medium">£</span>
                <div>
                  <p className="font-medium text-neutral-800">Prices</p>
                  <p className="text-neutral-600">Start from £10</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-neutral-800">What to bring</p>
                  <p className="text-neutral-600">Mat, blanket, water etc.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-neutral-800 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Begin or Return to Your Practice
            </h2>
            <p className="text-neutral-300 mb-8 max-w-xl mx-auto">
              If you would like to begin or return to your practice, you are very welcome.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-gold hover:bg-gold-dark text-white px-8 py-3.5 rounded-full font-medium transition-colors inline-flex items-center justify-center gap-2">
                Book a Class <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/yoga-classes/private-yoga" className="border border-white/30 hover:border-white text-white px-8 py-3.5 rounded-full font-medium transition-colors">
                Enquire about Private Sessions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing Line */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-neutral-700 italic">
            You don't need to be "flexible" or "good" at yoga to join my classes. You just need to be you.
          </p>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsStrip pageType="yoga-classes" title="What Our Yoga Community Says" />

      {/* Internal Links for SEO */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <p className="text-neutral-500 text-sm text-center mb-4">
            Explore specific yoga styles:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline text-sm">Hatha Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline text-sm">Yin Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/aerial-yoga" className="text-gold hover:underline text-sm">Aerial Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline text-sm">Beginner Yoga</Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default YogaClassesPage;
